import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { addYears } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';

const DEFAULT_MAX_DATE = addYears(new Date(), 20);
const DEFAULT_MIN_DATE = addYears(new Date(), -100);

const PICKER_TYPE = {
  MONTH: 'month',
  YEAR: 'year',
};

const MONTH_NAVIGATION = [
  { offset: -1, icon: ChevronLeft, ariaKey: 'prevButton' },
  { offset: 1, icon: ChevronRight, ariaKey: 'nextButton' },
];

function stopCalendarPropagation(event) {
  event.stopPropagation();
}

function getMonthNames(locale) {
  return [...Array(12).keys()].map((index) => locale.localize.month(index));
}

function createMonthOptions(monthNames) {
  return monthNames.map((label, value) => ({ label, value }));
}

function createYearOptions(lowerYearLimit, upperYearLimit) {
  return Array.from({ length: upperYearLimit - lowerYearLimit + 1 }, (_, index) => {
    const year = lowerYearLimit + index;
    return { label: String(year), value: year };
  });
}

function NavigatorPicker({
  value,
  options,
  onChange,
  ariaLabel,
  className,
  menuClassName,
  isOpen,
  onOpenChange,
  scrollSelectedToStart = false,
}) {
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const listId = useId();
  const selectedLabel = options.find((option) => option.value === value)?.label;

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const handleSelect = useCallback(
    (optionValue) => {
      onChange(optionValue);
      close();
    },
    [close, onChange],
  );

  useLayoutEffect(() => {
    if (!isOpen || !scrollSelectedToStart) return;

    const menu = menuRef.current;
    const selectedItem = menu?.querySelector('[aria-selected="true"]')?.closest('li');
    if (!menu || !selectedItem) return;

    menu.scrollTop = selectedItem.offsetTop;
  }, [isOpen, scrollSelectedToStart, value, options]);

  useEffect(() => {
    if (!isOpen) return;

    function handlePointerDown(event) {
      if (!containerRef.current?.contains(event.target)) close();
    }

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [close, isOpen]);

  return (
    <div ref={containerRef} className={cn('calendar-navigator-picker', className)}>
      <button
        type='button'
        className='calendar-navigator-picker__trigger'
        aria-label={ariaLabel}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        aria-controls={listId}
        onMouseDown={stopCalendarPropagation}
        onClick={() => onOpenChange(!isOpen)}
      >
        {selectedLabel}
      </button>
      {isOpen ? (
        <ul
          ref={menuRef}
          id={listId}
          role='listbox'
          className={cn('calendar-navigator-picker__menu', menuClassName)}
          onMouseDown={stopCalendarPropagation}
        >
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <li key={option.value} role='presentation'>
                <button
                  type='button'
                  role='option'
                  aria-selected={isSelected}
                  className={cn(
                    'calendar-navigator-picker__option',
                    isSelected && 'calendar-navigator-picker__option--selected',
                  )}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function NavigatorIconButton({ className, ariaLabel, onClick, disabled = false, children }) {
  return (
    <button
      type='button'
      className={className}
      aria-label={ariaLabel}
      disabled={disabled}
      onMouseDown={stopCalendarPropagation}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function CalendarNavigatorContent({ focusedDate, changeShownDate, props }) {
  const { showMonthArrow, minDate, maxDate, showMonthAndYearPickers, ariaLabels, locale } = props;
  const upperYearLimit = (maxDate || DEFAULT_MAX_DATE).getFullYear();
  const lowerYearLimit = (minDate || DEFAULT_MIN_DATE).getFullYear();
  const currentYear = focusedDate.getFullYear();

  const monthNames = useMemo(() => getMonthNames(locale), [locale]);
  const monthOptions = useMemo(() => createMonthOptions(monthNames), [monthNames]);
  const yearOptions = useMemo(
    () => createYearOptions(lowerYearLimit, upperYearLimit),
    [lowerYearLimit, upperYearLimit],
  );

  const [openPicker, setOpenPicker] = useState(null);
  const handlePickerOpenChange = useCallback((pickerType) => (isOpen) => setOpenPicker(isOpen ? pickerType : null), []);

  const pickers = useMemo(
    () => [
      {
        type: PICKER_TYPE.MONTH,
        value: focusedDate.getMonth(),
        options: monthOptions,
        ariaLabel: ariaLabels.monthPicker,
        onChange: (month) => changeShownDate(month, 'setMonth'),
      },
      {
        type: PICKER_TYPE.YEAR,
        value: currentYear,
        options: yearOptions,
        ariaLabel: ariaLabels.yearPicker,
        onChange: (year) => changeShownDate(year, 'setYear'),
        scrollSelectedToStart: true,
      },
    ],
    [
      ariaLabels.monthPicker,
      ariaLabels.yearPicker,
      changeShownDate,
      currentYear,
      focusedDate,
      monthOptions,
      yearOptions,
    ],
  );

  return (
    <div className='calendar-navigator' onMouseUp={stopCalendarPropagation}>
      {showMonthAndYearPickers ? (
        <div className='calendar-navigator__date-control'>
          {pickers.map(({ type, value, options, ariaLabel, onChange, scrollSelectedToStart }) => (
            <NavigatorPicker
              key={type}
              className={`calendar-navigator__${type}-picker`}
              menuClassName={`calendar-navigator-picker__menu--${type}`}
              value={value}
              options={options}
              ariaLabel={ariaLabel}
              isOpen={openPicker === type}
              onOpenChange={handlePickerOpenChange(type)}
              scrollSelectedToStart={scrollSelectedToStart}
              onChange={onChange}
            />
          ))}
          <NavigatorIconButton
            className='calendar-navigator__year-next-btn'
            ariaLabel='Next year'
            disabled={currentYear >= upperYearLimit}
            onClick={() => changeShownDate(currentYear + 1, 'setYear')}
          >
            <ChevronRight aria-hidden='true' />
          </NavigatorIconButton>
        </div>
      ) : (
        <span className='calendar-navigator__label'>
          {monthNames[focusedDate.getMonth()]} {currentYear}
        </span>
      )}

      {showMonthArrow ? (
        <div className='calendar-navigator__arrows'>
          {MONTH_NAVIGATION.map(({ offset, icon: Icon, ariaKey }) => (
            <NavigatorIconButton
              key={offset}
              className='calendar-navigator__arrow-btn'
              ariaLabel={ariaLabels[ariaKey]}
              onClick={() => changeShownDate(offset, 'monthOffset')}
            >
              <Icon aria-hidden='true' />
            </NavigatorIconButton>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function CalendarNavigator(focusedDate, changeShownDate, props) {
  return <CalendarNavigatorContent focusedDate={focusedDate} changeShownDate={changeShownDate} props={props} />;
}

export default CalendarNavigator;
